/**
 * Model — a GLB/GLTF mesh entity in the scene. Mirrors `Splat`'s pattern as
 * closely as the API allows so the rest of the editor (scene panel, transform
 * panel, picker, serializer) treats Models the same shape as Splats.
 *
 * Wraps a PlayCanvas Entity with the GLB instantiated under it. The container
 * asset itself is owned and disposed on destroy().
 *
 * NOTE: PlayCanvas's GLB loader (ContainerHandler) is already wired into the
 * PCApp resource handlers — see `src/pc-app.ts:154`. Component systems we need
 * (Render, AnimComponent — `pc-app.ts:100,97`) are also already on. So we get
 * GLB import for free; we just need to instantiate and book-keep.
 */

import { Asset, BoundingBox, Entity, Quat, Vec3 } from 'playcanvas';

import { Element, ElementType } from '../element';
import type { Serializer } from '../serializer';
import { Transform } from '../transform';

class Model extends Element {
    asset: Asset;
    entity: Entity;
    private _name: string;
    private _visible = true;
    private _worldBound = new BoundingBox();
    private _boundDirty = true;

    constructor(asset: Asset, rotation?: Quat) {
        super(ElementType.model);
        this.asset = asset;
        this._name = (asset.file as any)?.filename ?? asset.name ?? 'model';

        // PlayCanvas's container resource exposes instantiateRenderEntity()
        // which builds a full pc.Entity hierarchy with Render components for
        // every mesh in the GLB.
        const container = asset.resource as { instantiateRenderEntity: () => Entity };
        this.entity = container.instantiateRenderEntity();
        this.entity.name = `model:${this._name}`;
        if (rotation) this.entity.setLocalRotation(rotation);
    }

    destroy() {
        super.destroy();
        this.entity?.destroy();
        // Drop the container asset from the registry so its GPU resources are freed.
        try {
            this.asset.registry.remove(this.asset);
            this.asset.unload();
        } catch (err) {
            console.warn('[model] asset cleanup failed', err);
        }
    }

    add(): void {
        // SuperSplat's Scene adds Splats to `contentRoot` via the framework's
        // generic element flow. For Model we attach our entity to the same root.
        if (this.scene) {
            this.scene.contentRoot.addChild(this.entity);
            this._boundDirty = true;
            this.scene.forceRender = true;
        }
    }

    remove() {
        if (this.entity?.parent) {
            this.entity.parent.removeChild(this.entity);
        }
    }

    move(position?: Vec3, rotation?: Quat, scale?: Vec3) {
        if (position) this.entity.setLocalPosition(position);
        if (rotation) this.entity.setLocalRotation(rotation);
        if (scale) this.entity.setLocalScale(scale);
        this._boundDirty = true;
        if (this.scene) this.scene.forceRender = true;
    }

    get name(): string { return this._name; }
    set name(v: string) {
        if (v !== this._name) {
            this._name = v;
            this.entity.name = `model:${v}`;
            this.scene?.events.fire('model.name', this);
        }
    }

    get filename(): string { return (this.asset.file as any)?.filename ?? this._name; }

    get visible(): boolean { return this._visible; }
    set visible(v: boolean) {
        if (v === this._visible) return;
        this._visible = v;
        this.entity.enabled = v;
        if (this.scene) {
            this.scene.events.fire('model.visibility', this);
            this.scene.forceRender = true;
        }
    }

    get worldTransform() { return this.entity.getWorldTransform(); }

    // Whole-object editing only — Models don't have per-vertex selection state
    // like Splats do. EntityTransformHandler uses this to gate which handler
    // activates: 0 = whole-entity gizmo, >0 = sub-selection gizmo.
    get numSelected(): number { return 0; }

    /**
     * Mirror of Splat.getPivot for the transform handler. 'center' reads
     * local-space TRS; 'boundCenter' reads world-bound center.
     */
    getPivot(mode: 'center' | 'boundCenter', _selection: boolean, result: Transform): void {
        const { entity } = this;
        if (mode === 'center') {
            result.set(entity.getLocalPosition(), entity.getLocalRotation(), entity.getLocalScale());
        } else {
            const wb = this.worldBound;
            if (wb) {
                result.set(wb.center, entity.getLocalRotation(), entity.getLocalScale());
            } else {
                result.set(entity.getLocalPosition(), entity.getLocalRotation(), entity.getLocalScale());
            }
        }
    }

    // Splats expose these so the per-vertex texture lifecycle works; Models
    // don't have any, but stubs let the same upstream code paths run without
    // type-guards everywhere.
    selectionAlpha = 1;
    numSplats = 0;
    numDeleted = 0;
    numLocked = 0;

    /**
     * World-space AABB. Walks every render-component meshInstance under the
     * entity and unions their world AABBs. Recomputed lazily.
     */
    get worldBound(): BoundingBox | null {
        if (!this._boundDirty) return this._worldBound;
        let first = true;
        const acc = this._worldBound;
        const walk = (e: Entity) => {
            const render = (e as Entity & { render?: { meshInstances?: { aabb: BoundingBox }[] } }).render;
            if (render?.meshInstances) {
                for (const mi of render.meshInstances) {
                    if (first) { acc.copy(mi.aabb); first = false; }
                    else acc.add(mi.aabb);
                }
            }
            for (const child of e.children) {
                if (child instanceof Entity) walk(child);
            }
        };
        walk(this.entity);
        this._boundDirty = !first ? false : true;   // valid only if we hit at least one mesh
        return first ? null : acc;
    }

    serialize(_serializer: Serializer): void {
        // .ssproj persistence is a follow-up — for now, models are session-only.
        // When wired: serialize name, transform, asset filename (binary stored as
        // a sibling `model_N.glb` in the ZIP, same pattern as `splat_N.ply`).
    }
}

export { Model };
